import { timeout } from 'owa-sleep/lib/timeout';
import { schedule } from './schedule';
import type { ScheduleOptions } from './schedule';

interface AbstractTaskQueueConfig {
    taskDelay?: number;
    taskTimeout?: number; // Negative value means no timeout
    isUserBlocking?: boolean;
}

export default abstract class AbstractTaskQueue<Task> {
    protected abstract config: Required<AbstractTaskQueueConfig>;

    abstract add(value: any): void;
    abstract remove(): Task | undefined;
    abstract clear(): void;
    protected abstract get canRunMoreTasks(): boolean;
    protected abstract runTask(task: Task): Promise<void>;

    protected numberOfTasksRunning = 0;

    protected scheduleTask(): void {
        const taskDelay: number | ScheduleOptions = this.config.isUserBlocking
            ? { priority: 'user-blocking', delay: this.config.taskDelay }
            : this.config.taskDelay;
        if (this.canRunMoreTasks) {
            // Schedule the task on timer so that the JS thread is not occupied by the queue
            schedule(() => {
                this.tryRunTask();
            }, taskDelay);
        }
    }

    protected onTaskComplete(): void {
        this.numberOfTasksRunning--;
        this.scheduleTask();
    }

    protected tryRunTask(): void {
        if (this.canRunMoreTasks) {
            const task = this.remove();
            if (task) {
                this.numberOfTasksRunning++;

                if (this.config.taskTimeout > 0) {
                    timeout(
                        this.runTask(task),
                        this.config.taskTimeout,
                        new Error('Task timed out')
                    ).then(
                        () => {
                            this.onTaskComplete();
                        },
                        () => {
                            this.onTaskComplete();
                        }
                    );
                } else {
                    this.runTask(task).then(() => {
                        this.onTaskComplete();
                    });
                }
            }

            // Try scheduling parallel tasks
            this.scheduleTask();
        }
    }
}
